import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { getSaleTypeTrackingValue, SaleType } from '@models/Products';
import { capitalizeFirstLetter } from '@utils/StringUtils';
import {
  addAllProductGroupFilter,
  addCountryLoadedTagInPlace,
  addPgLoadedTagInPlace,
  getGeneralCollectionInsightTags,
  getIsUsingCollectionLiveData,
  getPeriodLabel,
} from '@utils/collectionUtils/CollectionUtils';
import { PerformanceAnalysisLineChartInsight } from './Model';
import { PageName } from '@utils/pageConfig/Types';
import {  getBaseTimePeriodForMATAndYTD, getPeriodicityForMATAndYTD, getTrimmedMonthName } from '@utils/PeriodUtils';
import { getCountryCodes } from '@utils/CountryUtils';
import { getRequireAllPgLoadedInCollections } from '@components/collections/CollectionsFlagsByInsightType';
import { CollectionInsightType } from '@components/collections/Model';
import { ProductsMarketRetailerLineChartInsight } from '../products-trends-market-and-retailer-line-chart/Model';

export const getPerformanceAnalysisLineChartInsightInfoTags = async (
  insight: PerformanceAnalysisLineChartInsight | ProductsMarketRetailerLineChartInsight,
) => {
  const {
    apiParams: {
      clientId,
      countries,
      productGroup,
      productType,
      period,
      comparisonPeriod,
      selectedGrowthType,
      appliedFilters,
      periodType,
      pageName,
    },
    selectedLineChartSaleType,
    selectedFeature,
    selectedRetailerKpiOption,
    retailerClientName,
    collectionDataType,
  } = insight;

  const baseTimePeriod = getBaseTimePeriodForMATAndYTD(period, periodType);
  const { periodicity } = getPeriodicityForMATAndYTD(baseTimePeriod, periodType);
  const requireAllPgLoadedInCollections = getRequireAllPgLoadedInCollections(CollectionInsightType.ExploreLineChart);

  const periodLabel = await getPeriodLabel({
    clientId,
    periodicity,
    baseTimePeriod,
    comparisonPeriod,
    countryIds: getCountryCodes(countries),
    productType,
    collectionDataType: getIsUsingCollectionLiveData(period, collectionDataType) ? 'live' : collectionDataType,
    productGroups: productGroup,
    requireAllPgLoadedInCollections: requireAllPgLoadedInCollections,
  });

  const generalCollectionInsightTags = getGeneralCollectionInsightTags({
    periodType: periodicity,
    comparisonPeriod:
      typeof comparisonPeriod === 'object' && comparisonPeriod !== null
        ? JSON.stringify(comparisonPeriod)
        : String(comparisonPeriod),
    selectedGrowthType,
    appliedFilters: addAllProductGroupFilter(appliedFilters),
    retailerClientName,
    period: periodLabel,
  });

  const baseApiParams = {
    clientId,
    countries: countries,
    productType,
    period: period,
    comparisonPeriod: RelativePeriod.PreviousYearCurrentPeriod,
    productGroup: productGroup ?? [],
    periodType: periodicity,
    appliedFilters: [],
  };
  const { productsParam, ...filteredApiParams } = insight.apiParams as any;
  const isSelectSingleCountry = countries?.length === 1;
  if (isSelectSingleCountry) {
    const currPeriod = filteredApiParams.period;
    filteredApiParams.period = getTrimmedMonthName(currPeriod); // converting June 2023 to Jun 2023
    await addPgLoadedTagInPlace(generalCollectionInsightTags, filteredApiParams, collectionDataType);
  } else {
    await addCountryLoadedTagInPlace(generalCollectionInsightTags, baseApiParams, collectionDataType);
  }

  let finalLineChartInsightInfoTags = [
    ...generalCollectionInsightTags,
    {
      key: 'Show',
      value: capitalizeFirstLetter(getSaleTypeTrackingValue(selectedLineChartSaleType)[selectedLineChartSaleType]),
    },
  ];

  if (pageName === PageName.RI_PRODUCT || pageName === PageName.RI_PRODUCT_TRENDS) {
    finalLineChartInsightInfoTags = finalLineChartInsightInfoTags.filter(
      (tag) => !(tag.key === 'Info' && tag.value.includes('Category loaded')),
    );
  } else {
    finalLineChartInsightInfoTags.push({
      key: 'ShowPerformanceWithin',
      value: capitalizeFirstLetter(selectedFeature.value),
    });
  }

  if (pageName === PageName.EXPLORE) {
    finalLineChartInsightInfoTags = [
      ...generalCollectionInsightTags,
      {
        key: 'Show',
        value: capitalizeFirstLetter(selectedFeature.value),
      },
      {
        key: 'By',
        value: capitalizeFirstLetter(insight.selectedLineChartSaleType),
      },
    ];
  }

  if (selectedLineChartSaleType !== SaleType.price) {
    finalLineChartInsightInfoTags.push({
      key: 'KPI',
      value: `Retailer ${capitalizeFirstLetter(selectedRetailerKpiOption)}`,
    });
  }

  return finalLineChartInsightInfoTags;
};
